/**
 * ModeSliderDiffView — behavior-switch preview for a `mode_slider_config`
 * proposal (`propose_edit_agent(field='mode_slider_config')`).
 *
 * The AoA proposes a whole ModeConfig JSON. Rendering that as a text diff
 * asks the reviewer to mentally reconstruct a UI control from JSON keys.
 * Instead this view shows, at the top, a READ-ONLY replica of the chat's
 * behavior switch exactly as the end user will see it (same classes as
 * conversations/ModeSelector — that CSS is imported, the component itself
 * is untouched), with `default_index` rendered as the active segment.
 * Below it, one block per mode spells out the label, its tooltip
 * description, and the `instruction` that gets injected into the AI when
 * that mode is active — the same information the edit-agent page's
 * behavior-switching tab shows.
 *
 * Unlike model proposals, Edit stays AVAILABLE for this field: the config
 * is free text (labels / instructions), not a constrained registry id.
 */

import React from 'react';
import { useLanguage } from '@hooks/useLanguage';
import '@modules/conversations/components/ModeSelector.css';
import './ModeSliderDiffView.css';

export interface ModeConfigLike {
  enabled?: boolean;
  title?: string;
  description?: string;
  default_index?: number;
  modes?: Array<{ label?: string; description?: string; instruction?: string }>;
}

interface Props {
  /** Current config (parsed from proposal.old_value; may be null). */
  before: ModeConfigLike | null;
  /** Proposed config (parsed from proposal.new_value). */
  after: ModeConfigLike;
}

export const ModeSliderDiffView: React.FC<Props> = ({ before, after }) => {
  const { t } = useLanguage();
  const modes = Array.isArray(after.modes) ? after.modes : [];
  const beforeModes = before && Array.isArray(before.modes) ? before.modes : [];
  const hadSwitch = !!before && before.enabled !== false && beforeModes.length > 0;

  // Proposal turns the switch OFF (or ships no modes) — nothing to
  // preview; a one-line note says it plainly.
  if (after.enabled === false || modes.length === 0) {
    return (
      <div className="aoa-modeslider" data-testid="aoa-modeslider-diff">
        <div className="aoa-modeslider__note">
          {t('metaAgent.proposal.modeSlider.turnsOffNote')}
        </div>
      </div>
    );
  }

  const defaultIdx =
    typeof after.default_index === 'number' &&
    after.default_index >= 0 &&
    after.default_index < modes.length
      ? after.default_index
      : 0;

  return (
    <div className="aoa-modeslider" data-testid="aoa-modeslider-diff">
      {/* Read-only replica of the in-chat control. Same markup/classes as
          ModeSelector so it can't drift visually; pointer-events are off
          via .aoa-modeslider__replica. */}
      <div className="aoa-modeslider__heading">
        {t('metaAgent.proposal.modeSlider.previewHeading')}
      </div>
      <div className="mode-selector-inline aoa-modeslider__replica" aria-hidden>
        {after.title && (
          <span className="mode-selector-inline-title" dir="auto">{after.title}</span>
        )}
        <div className="mode-selector-segments">
          {modes.map((mode, idx) => (
            <button
              key={idx}
              type="button"
              tabIndex={-1}
              className={`mode-selector-btn ${idx === defaultIdx ? 'active' : ''}`}
            >
              {mode.label || String(idx + 1)}
            </button>
          ))}
        </div>
      </div>

      {/* One-line "what changes" note when the agent already had a switch —
          the replica above is the star; this just anchors the comparison. */}
      {hadSwitch && (
        <div className="aoa-modeslider__note">
          {t('metaAgent.proposal.modeSlider.replacesNote', {
            before: beforeModes.length,
            after: modes.length,
          })}
        </div>
      )}

      {/* Per-mode detail: label, tooltip description, injected prompt. */}
      <div className="aoa-modeslider__modes">
        {modes.map((mode, idx) => (
          <div key={idx} className="aoa-modeslider__mode">
            <div className="aoa-modeslider__mode-label">
              {mode.label || String(idx + 1)}
              {idx === defaultIdx && (
                <span className="aoa-modeslider__default-tag">
                  {t('metaAgent.proposal.modeSlider.defaultTag')}
                </span>
              )}
            </div>
            {mode.description && (
              <div className="aoa-modeslider__mode-desc" dir="auto">{mode.description}</div>
            )}
            {mode.instruction && (
              <div className="aoa-modeslider__mode-instruction">
                <div className="aoa-modeslider__instruction-label">
                  {t('metaAgent.proposal.modeSlider.injectedPrompt')}
                </div>
                <div className="aoa-modeslider__instruction-text" dir="auto">
                  {mode.instruction}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
